import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import {
  archiveSandboxConversation,
  createSandboxConversation,
  listSandboxConversations,
} from "@/api/sandbox";
import type { SandboxConversationSummary } from "@/types/sandbox";

type SandboxConversationsContextValue = {
  conversations: SandboxConversationSummary[];
  isLoading: boolean;
  loadFailed: boolean;
  activeConversationId: number | null;
  loadConversations: () => Promise<void>;
  selectConversation: (id: number) => void;
  createConversation: () => Promise<void>;
  archiveConversation: (id: number) => Promise<void>;
  updateConversationTitle: (id: number, title: string) => void;
};

const SandboxConversationsContext = createContext<SandboxConversationsContextValue | null>(null);

/** Shares the user's sandbox threads between the sidebar list and the chat pane. */
export function SandboxConversationsProvider({ children }: { children: React.ReactNode }) {
  const [conversations, setConversations] = useState<SandboxConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const loadRequestedRef = useRef(false);

  // The provider wraps the whole app shell, including users without sandbox
  // access, so the list is fetched when it is first shown, not on mount.
  const loadConversations = useCallback(async () => {
    if (loadRequestedRef.current) return;
    loadRequestedRef.current = true;
    setIsLoading(true);
    setLoadFailed(false);
    try {
      setConversations(await listSandboxConversations());
    } catch {
      setLoadFailed(true);
      loadRequestedRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createConversation = useCallback(async () => {
    const created = await createSandboxConversation({ title: "New Conversation" });
    setConversations((previous) => [created, ...previous]);
    setActiveConversationId(created.id);
  }, []);

  const archiveConversation = useCallback(async (id: number) => {
    await archiveSandboxConversation(id);
    setConversations((previous) => previous.filter((conversation) => conversation.id !== id));
    setActiveConversationId((previous) => (previous === id ? null : previous));
  }, []);

  const updateConversationTitle = useCallback((id: number, title: string) => {
    setConversations((previous) =>
      previous.map((conversation) => (conversation.id === id ? { ...conversation, title } : conversation)),
    );
  }, []);

  const value = useMemo(
    () => ({
      conversations,
      isLoading,
      loadFailed,
      activeConversationId,
      loadConversations,
      selectConversation: setActiveConversationId,
      createConversation,
      archiveConversation,
      updateConversationTitle,
    }),
    [
      conversations,
      isLoading,
      loadFailed,
      activeConversationId,
      loadConversations,
      createConversation,
      archiveConversation,
      updateConversationTitle,
    ],
  );

  return <SandboxConversationsContext.Provider value={value}>{children}</SandboxConversationsContext.Provider>;
}

export function useSandboxConversations() {
  const ctx = useContext(SandboxConversationsContext);
  if (!ctx) {
    throw new Error("useSandboxConversations must be used within SandboxConversationsProvider");
  }
  return ctx;
}
